import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Search,
  Clock,
  Building2,
  Zap,
  Crown,
  Gift,
  MoreHorizontal,
  Trash2,
  Key,
  Ban,
  PlayCircle,
  XCircle,
} from 'lucide-react';

const PLAN_COLORS = {
  trial: 'bg-slate-500',
  base: 'bg-blue-600',
  pro: 'bg-orange-500',
  business: 'bg-purple-600',
  cancelled: 'bg-red-600',
};

const PLAN_ICONS = {
  trial: Clock,
  base: Building2,
  pro: Zap,
  business: Crown,
};

export default function AdminUsersTable({
  users,
  searchTerm,
  setSearchTerm,
  onSearch,
  onGrantClick,
  onRevoke,
  onPasswordResetClick,
  onSuspendClick,
  onReactivate,
  onCancelSubscription,
  onDeleteClick,
  formatDate,
}) {
  return (
    <Card className="rounded-sm bg-slate-800 border-slate-700">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-lg">All Users</CardTitle>
          <div className="flex gap-2">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                className="pl-10 rounded-sm bg-slate-700 border-slate-600"
              />
            </div>
            <Button onClick={onSearch} className="rounded-sm bg-slate-700 hover:bg-slate-600">
              Search
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-400">User</TableHead>
                <TableHead className="text-slate-400">Company</TableHead>
                <TableHead className="text-slate-400">Plan</TableHead>
                <TableHead className="text-slate-400">Stats</TableHead>
                <TableHead className="text-slate-400">Joined</TableHead>
                <TableHead className="text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const PlanIcon = PLAN_ICONS[user.subscription.plan_id] || Clock;
                const hasGrant = user.subscription.free_grant;
                return (
                  <TableRow key={user.id} className="border-slate-700">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-slate-700 rounded-sm flex items-center justify-center font-bold">
                          {user.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-slate-400">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {user.company || <span className="text-slate-500">-</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={PLAN_COLORS[user.subscription.plan_id]}>
                          <PlanIcon className="h-3 w-3 mr-1" />
                          {user.subscription.plan_id}
                        </Badge>
                        {hasGrant && (
                          <Badge variant="outline" className="border-pink-500 text-pink-400">
                            <Gift className="h-3 w-3 mr-1" />
                            Free
                          </Badge>
                        )}
                        {user.status === 'suspended' && (
                          <Badge className="bg-red-600">
                            <Ban className="h-3 w-3 mr-1" />
                            Suspended
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm space-y-1">
                        <p><span className="text-slate-400">Team:</span> {user.stats.team_members}</p>
                        <p><span className="text-slate-400">Leads:</span> {user.stats.leads}</p>
                        <p><span className="text-slate-400">Projects:</span> {user.stats.projects}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {formatDate(user.created_at)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                          {hasGrant ? (
                            <DropdownMenuItem 
                              className="text-red-400 focus:text-red-300 focus:bg-slate-700"
                              onClick={() => onRevoke(user.email)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Revoke Grant
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              className="text-green-400 focus:text-green-300 focus:bg-slate-700"
                              onClick={() => onGrantClick(user)}
                            >
                              <Gift className="h-4 w-4 mr-2" />
                              Grant Free Plan
                            </DropdownMenuItem>
                          )}
                          
                          <DropdownMenuItem 
                            className="text-yellow-400 focus:text-yellow-300 focus:bg-slate-700"
                            onClick={() => onPasswordResetClick(user)}
                          >
                            <Key className="h-4 w-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                          
                          <DropdownMenuSeparator className="bg-slate-700" />
                          
                          {user.status === 'suspended' ? (
                            <DropdownMenuItem 
                              className="text-green-400 focus:text-green-300 focus:bg-slate-700"
                              onClick={() => onReactivate(user.email)}
                            >
                              <PlayCircle className="h-4 w-4 mr-2" />
                              Reactivate Account
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              className="text-orange-400 focus:text-orange-300 focus:bg-slate-700"
                              onClick={() => onSuspendClick(user)}
                            >
                              <Ban className="h-4 w-4 mr-2" />
                              Suspend Account
                            </DropdownMenuItem>
                          )}
                          
                          {user.subscription.plan_id !== 'trial' && user.subscription.plan_id !== 'cancelled' && (
                            <DropdownMenuItem 
                              className="text-red-400 focus:text-red-300 focus:bg-slate-700"
                              onClick={() => onCancelSubscription(user.email)}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancel Subscription
                            </DropdownMenuItem>
                          )}
                          
                          <DropdownMenuSeparator className="bg-slate-700" />
                          
                          <DropdownMenuItem 
                            className="text-red-500 focus:text-red-400 focus:bg-slate-700"
                            onClick={() => onDeleteClick(user)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
